/**
 * As regras da tela de cadastro de números de WhatsApp (`/whatsapp`).
 *
 * Estão fora do componente porque cada uma delas responde por um jeito de o
 * cadastro do cliente parar de funcionar SEM ERRO NENHUM na tela -- e falha
 * silenciosa é justamente o que não se testa renderizando e clicando:
 *
 *   - mandar `token: ''` ao corrigir um acento no rótulo apagaria a credencial
 *     e o número ficaria mudo (a API trata branco como "manter", mas depender
 *     dessa gentileza é depender de uma decisão do outro repositório);
 *   - mandar `usa_app_proprio: false` junto de um segredo novo é 422 de
 *     propósito, e o cliente que digitou o segredo e depois desmarcou a opção
 *     não tem como entender esse erro;
 *   - omitir `departamentos` MANTÉM o menu na API, então "tirei todos os
 *     departamentos e salvei" não teria efeito algum;
 *   - a ordem do array `departamentos` É a numeração que o cliente final lê no
 *     WhatsApp, então ordenar pelo array cru em vez de pela `ordem` do pivô
 *     reembaralha o menu de quem já estava atendendo;
 *   - mostrar a URL por slug para quem usa o NOSSO app é dar ao cliente um
 *     endereço que responde 403 a toda mensagem.
 *
 * Nenhuma credencial passa por aqui em sentido de LEITURA: a API não devolve
 * `token` nem `app_secret`, e a tela não tem o que mostrar além de `tem_token`.
 */

/** Limite do corpo da mensagem interativa na Meta -- o mesmo do controller. */
export const LIMITE_DA_SAUDACAO = 1024;

/**
 * Quantas opções a Meta aceita numa lista interativa.
 *
 * Irmão do `LIMITE_DA_SAUDACAO`, e pela mesma razão: `EnvioPelaMeta` corta o
 * excedente com `array_slice` para a Meta não RECUSAR a mensagem inteira, e esse
 * corte é a última linha de defesa -- quem salvou 11 departamentos não vê erro
 * nenhum, o 11º simplesmente não existe no menu do cliente, e não há sintoma
 * para relatar. Não é borda: o bot real da SysFar tem 11 menus.
 */
export const LIMITE_DE_DEPARTAMENTOS = 10;

/**
 * Quantos caracteres a Meta aceita no título de uma opção.
 *
 * Diferente dos outros dois: aqui a tela AVISA e deixa salvar, não recusa. O
 * corte de `EnvioPelaMeta` sempre produz um menu que funciona (a Meta recusaria
 * a mensagem se o título passasse, e o corte é o que evita isso), então o
 * estrago é cosmético -- exceto quando dois departamentos coincidem nos
 * primeiros 24 caracteres e viram duas opções visualmente idênticas para quem lê
 * no WhatsApp.
 *
 * Recusar transformaria esse problema cosmético numa trava dura no MESMO `PUT`
 * que troca um token vazado ou desativa o número: a tela manda `departamentos`
 * em todo salvamento, então um nome comprido cadastrado meses atrás impediria a
 * ação urgente. Avisar dá ao admin o que ele precisa (qual renomear, ou em qual
 * preencher o "nome para o cliente") sem bloquear o resto.
 */
export const LIMITE_DO_TITULO = 24;

/**
 * A recusa por falta de permissão, em texto de gente.
 *
 * Ela existe porque o 403 aqui é ESPERADO e não é defeito de tela: o item do
 * menu é liberado por um caminho de permissão mais antigo que o da API, e
 * alinhar os dois mexeria no menu de todos os clientes (decisão de produto,
 * fora desta tarefa). O que não pode acontecer é a tela tratar esse 403 como
 * "a lista veio vazia" -- o cliente leria "nenhum número cadastrado" e iria
 * cadastrar de novo um número que já existe.
 */
export const MENSAGEM_SEM_PERMISSAO =
    'Você não tem permissão para gerenciar números de WhatsApp. Peça a um administrador '
    + 'para liberar a tela de WhatsApp no seu perfil.';

const texto = (valor) => (typeof valor === 'string' ? valor.trim() : '');

const preenchido = (valor) => texto(valor) !== '';

/**
 * Em que pé está o número, na ordem do primeiro conserto.
 *
 * A precedência não é estética. Um número desativado não RECEBE nada, e é esse
 * o sintoma que o cliente relata; dizer "sem credencial" para ele mandaria
 * cadastrar um token que não muda nada. E sem token o número é mudo, então
 * montar o menu antes também não adianta. Cada estado é o PRÓXIMO passo de quem
 * está cadastrando.
 *
 * "Não recebe", e não "não recebe nem envia": desativar para a ENTRADA. As
 * conversas já estampadas com este número continuam sendo respondidas por ele
 * (ver `mensagemDoConflito`), então um número inativo não é um número calado.
 */
export const estadoDoNumero = (numero) => {
    if (!numero?.ativo) return 'inativo';
    if (!numero?.tem_token) return 'sem credencial';
    if (!numero?.departamentos?.length) return 'sem departamentos';
    return 'ativo';
};

/**
 * A cor do chip de estado, como TOKEN da paleta do MUI.
 *
 * Token, e nunca hex: é o que faz o chip continuar legível quando o usuário
 * troca para o tema escuro. Cor fixa aqui já mordeu este projeto antes.
 */
/**
 * Tom LEGÍVEL da cor de estado em cada tema.
 *
 * Medido, não escolhido: `warning.main` (`#d97706`) sobre o papel branco do
 * tema claro dá 3,19:1 -- abaixo dos 4,5:1 que texto pequeno precisa. O tom
 * `dark` no tema claro dá 5,91:1 e o `main` no escuro dá 9,64:1; os dois
 * passam. Usar `color="warning"` direto no Chip deixaria o rótulo apagado
 * justamente no tema em que o aviso importa.
 *
 * Mora aqui, e não na página, porque a tela de templates precisa do MESMO
 * tom para os status dela (aprovado, em análise, rejeitado). Uma segunda
 * cópia desta medição divergiria na primeira correção feita só numa das
 * duas -- e o sintoma seria texto ilegível num dos temas, que é exatamente o
 * que ela existe para impedir.
 */
export const tomLegivel = (familia) => (tema) => (
    familia === 'default'
        ? tema.palette.text.primary
        : tema.palette[familia][tema.palette.mode === 'dark' ? 'main' : 'dark']
);

export const corDoEstado = (estado) => {
    if (estado === 'ativo') return 'success';
    if (estado === 'sem departamentos') return 'warning';
    if (estado === 'sem credencial') return 'error';
    return 'default';
};

/**
 * O que a tela pode dizer sobre o token.
 *
 * Só se ele EXISTE. Não há máscara com os últimos dígitos porque não há dígito
 * nenhum: a API não devolve o valor, nem parcial.
 */
export const textoDaCredencial = (numero) =>
    (numero?.tem_token ? 'credencial cadastrada' : 'falta a credencial');

const semBarraFinal = (base) => texto(base).replace(/\/+$/, '');

/** A porta global do webhook -- a que serve para quem usa o NOSSO app na Meta. */
export const urlGlobalDoWebhook = (baseDaApi) => {
    const base = semBarraFinal(baseDaApi);

    return base === '' ? null : `${base}/webhooks/whatsapp`;
};

/**
 * A ÚNICA URL de webhook que serve para este número, ou `null`.
 *
 * Duas portas existem e só uma funciona para cada número: a global confere a
 * assinatura com o segredo da plataforma, a por slug confere com o
 * `app_secret` do próprio número. Mostrar a errada não dá erro na tela -- dá
 * 403 em toda mensagem que a Meta tentar entregar, e o sintoma que o cliente
 * relata é "não chega nada".
 *
 * `null` no app próprio sem slug é de propósito: o controller não gera um sem o
 * outro, então esse par é cadastro incoerente. Cair na URL global ali seria
 * oferecer um endereço que o app do cliente não valida.
 */
export const urlDoWebhook = (numero, baseDaApi) => {
    if (!numero?.usa_app_proprio) {
        return urlGlobalDoWebhook(baseDaApi);
    }

    if (preenchido(numero.webhook_url)) return texto(numero.webhook_url);

    if (preenchido(numero.webhook_slug)) {
        const base = semBarraFinal(baseDaApi);

        return base === '' ? null : `${base}/webhooks/whatsapp/${texto(numero.webhook_slug)}`;
    }

    return null;
};

/**
 * Os departamentos do número NA ORDEM DO MENU.
 *
 * Pela `ordem` do pivô, não pela ordem em que o array chegou: é essa sequência
 * que o cliente final lê numerada no WhatsApp.
 *
 * Existe separada de `idsDosDepartamentos` porque a LISTA da tela precisa dos
 * nomes, não só dos ids, e antes ela numerava os chips pela ordem crua do
 * array. Coincidia, porque a relação no servidor tem `orderBy` pela `ordem` --
 * mas no dia em que aquele `orderBy` saísse, o cartão passaria a mentir sobre
 * "menu que o cliente vê" enquanto o formulário continuaria certo: duas telas
 * da mesma feature divergindo em silêncio.
 */
export const departamentosDoNumero = (numero) => {
    const lista = Array.isArray(numero?.departamentos) ? [...numero.departamentos] : [];

    return lista.sort((a, b) => (Number(a?.ordem) || 0) - (Number(b?.ordem) || 0));
};

/** Só os ids, na mesma ordem -- é o que o formulário edita e devolve. */
export const idsDosDepartamentos = (numero) => departamentosDoNumero(numero)
    .map((departamento) => departamento?.id)
    .filter((id) => Number.isInteger(id));

/**
 * Troca um departamento de lugar no menu, sem mexer na lista recebida.
 *
 * A guarda de faixa é o clique em "subir" no primeiro item e em "descer" no
 * último: sem ela o `splice` tiraria o item da lista e um departamento
 * DESAPARECERIA do menu por causa de um clique que o cliente esperava que não
 * fizesse nada.
 */
export const moverDepartamento = (ids, de, para) => {
    const lista = Array.isArray(ids) ? [...ids] : [];

    if (de < 0 || de >= lista.length) return lista;
    if (para < 0 || para >= lista.length) return lista;

    const [item] = lista.splice(de, 1);
    lista.splice(para, 0, item);

    return lista;
};

/**
 * O que falta preencher, por campo -- `{}` quando está tudo certo.
 *
 * Espelha a validação da API para que a queixa apareça ANTES do round-trip,
 * com uma diferença que é a razão de existir da função: na EDIÇÃO o token em
 * branco é válido. O campo chega necessariamente vazio (a API nunca devolveu o
 * valor), e cobrá-lo obrigaria a redigitar a credencial da Meta cada vez que
 * alguém corrigisse um acento no rótulo.
 *
 * `tinhaAppProprio` é o estado do número ANTES da edição: quem já tem segredo
 * guardado pode salvar com o campo em branco (mantém), quem está LIGANDO app
 * próprio agora precisa informar o segredo -- sem ele, o slug ficaria de pé
 * sem par e o número responderia 403 a toda mensagem.
 */
export const errosDoFormulario = (
    form,
    { edicao = false, tinhaAppProprio = false, trocarToken = false, trocarSegredo = false } = {},
) => {
    const erros = {};

    if (!preenchido(form?.phone_number_id)) {
        erros.phone_number_id = 'Informe o ID do número (phone_number_id) da Meta.';
    }
    if (!preenchido(form?.waba_id)) {
        erros.waba_id = 'Informe o ID da conta do WhatsApp Business (waba_id).';
    }
    if (!preenchido(form?.display_number)) {
        erros.display_number = 'Informe o número como o cliente o vê.';
    }
    if (!preenchido(form?.rotulo)) {
        erros.rotulo = 'Informe um rótulo para diferenciar este número dos outros.';
    }
    if (texto(form?.saudacao).length > LIMITE_DA_SAUDACAO) {
        erros.saudacao = `A saudação passa de ${LIMITE_DA_SAUDACAO} caracteres, o limite da Meta.`;
    }
    // Mesmo teto e pelo mesmo motivo: são textos que o cliente lê no
    // WhatsApp, e a Meta RECUSA a mensagem inteira acima de 1024.
    if (texto(form?.mensagem_pos_departamento).length > LIMITE_DA_SAUDACAO) {
        erros.mensagem_pos_departamento = `A mensagem passa de ${LIMITE_DA_SAUDACAO} caracteres, o limite da Meta.`;
    }
    if (texto(form?.mensagem_de_encerramento).length > LIMITE_DA_SAUDACAO) {
        erros.mensagem_de_encerramento = `A mensagem passa de ${LIMITE_DA_SAUDACAO} caracteres, o limite da Meta.`;
    }
    if (!edicao && !preenchido(form?.token)) {
        erros.token = 'Informe o token de acesso da Meta para este número.';
    }
    // Pediu para trocar e deixou em branco: sem esta queixa o salvamento seria
    // um NADA silencioso (a chave é omitida do corpo), e quem clicou em "trocar
    // o token" sairia da tela achando que trocou.
    if (edicao && trocarToken && !preenchido(form?.token)) {
        erros.token = 'Informe o token novo, ou cancele a troca para manter o atual.';
    }
    if (form?.usa_app_proprio && !preenchido(form?.app_secret) && !(edicao && tinhaAppProprio)) {
        erros.app_secret = 'Com app próprio o segredo do app (app_secret) é obrigatório.';
    }
    if (edicao && trocarSegredo && form?.usa_app_proprio && !preenchido(form?.app_secret)) {
        erros.app_secret = 'Informe o segredo novo, ou cancele a troca para manter o atual.';
    }
    // Espelha o `max:10` da API. Sem esta queixa o cliente recebia o 422 do
    // servidor -- que é melhor que o corte silencioso de antes, mas depois de um
    // round-trip e sem dizer que o limite não é nosso.
    if ((Array.isArray(form?.departamentos) ? form.departamentos.length : 0) > LIMITE_DE_DEPARTAMENTOS) {
        erros.departamentos = `O menu aceita no máximo ${LIMITE_DE_DEPARTAMENTOS} departamentos — `
            + 'é limite da Meta, não nosso: com mais opções ela recusa a mensagem inteira e o '
            + 'cliente não recebe menu nenhum. Tire os que sobram.';
    }

    return erros;
};

/**
 * O título que a Meta vai mostrar para este departamento.
 *
 * MESMA fórmula de `ServiceGroup::visiveisNoNumero()` na API
 * (`client_display_name ?: description`), e é por isso que ela mora aqui e não
 * inline no componente: a tela lista os departamentos pelo `description` (é o
 * nome que o operador conhece), mas quem vai para o WhatsApp é o
 * `client_display_name` quando ele existe. Avisar sobre o comprimento do nome
 * ERRADO seria avisar sobre um texto que ninguém vê.
 */
export const tituloNoMenu = (grupo) => texto(grupo?.client_display_name) || texto(grupo?.description);

/**
 * Os avisos do menu -- lista vazia quando não há nada a dizer.
 *
 * AVISO, e não erro: nada aqui impede o salvamento (ver `LIMITE_DO_TITULO`). O
 * que ele impede é o admin descobrir o corte pela reclamação de quem recebeu o
 * menu, que é o único jeito de descobrir hoje.
 *
 * Nomeia os departamentos culpados porque é a única informação que resolve: o
 * admin precisa saber em QUAL mexer -- encurtar o nome, ou preencher o "nome
 * para o cliente" dele em Configurações, que existe justamente para isso ("ADM"
 * vira "Administrativo", "Suporte N1" vira "Suporte").
 *
 * Departamento que não está na lista carregada é ignorado em silêncio de
 * propósito: é o caso do `GET /groups` que falhou (a tela já avisa disso em
 * outro lugar) e o de um vínculo com departamento desativado. Inventar aviso
 * sobre um título que a tela não conhece seria pior que não avisar.
 */
export const avisosDoMenu = (ids, grupos) => {
    const lista = Array.isArray(ids) ? ids : [];
    const porId = new Map();

    (Array.isArray(grupos) ? grupos : []).forEach((grupo) => {
        if (grupo?.id != null) porId.set(Number(grupo.id), grupo);
    });

    const longos = lista
        .map((id) => porId.get(Number(id)))
        .filter((grupo) => grupo !== undefined)
        .map(tituloNoMenu)
        .filter((titulo) => titulo.length > LIMITE_DO_TITULO);

    if (longos.length === 0) return [];

    return [
        `No WhatsApp a Meta corta o nome da opção em ${LIMITE_DO_TITULO} caracteres, e `
        + `${longos.length === 1 ? 'este passa' : 'estes passam'} do limite: ${longos.join(', ')}. `
        + 'O menu continua funcionando, mas o nome aparece cortado — e dois departamentos que '
        + 'comecem igual viram duas opções idênticas para o cliente. Encurte o nome ou preencha o '
        + '"nome para o cliente" do departamento em Configurações.',
    ];
};

/**
 * O corpo da requisição, montado a partir do que a tela tem na mão.
 *
 * Três omissões são deliberadas e nenhuma é economia de bytes:
 *
 *   - `token`/`app_secret` em branco NÃO vão. Em branco significa "mantenha o
 *     que está guardado", e a forma de dizer isso sem depender de como o
 *     servidor interpreta string vazia é não mandar a chave.
 *   - `app_secret` nunca acompanha `usa_app_proprio: false`. A API recusa o par
 *     com 422 de propósito (é contraditório), e quem digitou o segredo e depois
 *     desmarcou a opção não teria como entender esse erro.
 *   - `webhook_slug`, `webhook_url`, `tem_token` e `id` nunca vão. Os três
 *     primeiros são derivados no servidor; o cliente escolhe SE usa app
 *     próprio, não qual é o endereço dele.
 *
 * `ativo` vai sempre como booleano de verdade: em branco a API não desativa,
 * mas também não ATIVA, e aí o botão da tela não valeria nada.
 *
 * ## O gesto explícito
 *
 * Na EDIÇÃO, a credencial só entra no corpo quando `trocarToken` /
 * `trocarSegredo` dizem que a pessoa pediu para trocar -- não basta o campo ter
 * texto. O `autoComplete="new-password"` da tela é uma DICA, e dica é o que
 * gerenciador de senha de terceiro (1Password, LastPass) ignora: eles preenchem
 * `input[type=password]` de forma agressiva, o `onChange` dispara, e o token
 * guardado seria sobrescrito em silêncio por um valor que ninguém digitou.
 *
 * Aqui a chave simplesmente não existe sem o gesto, então nada que a extensão
 * faça com o campo chega ao `PUT`. São duas defesas independentes e as duas
 * ficam: a dica evita o preenchimento, esta regra evita o estrago quando a dica
 * for ignorada.
 */
export const corpoDoFormulario = (
    form,
    { edicao = false, trocarToken = false, trocarSegredo = false } = {},
) => {
    const usaAppProprio = Boolean(form?.usa_app_proprio);
    const saudacao = texto(form?.saudacao);
    const posDepartamento = texto(form?.mensagem_pos_departamento);
    const encerramento = texto(form?.mensagem_de_encerramento);
    const token = texto(form?.token);
    const segredo = texto(form?.app_secret);

    const corpo = {
        phone_number_id: texto(form?.phone_number_id),
        waba_id: texto(form?.waba_id),
        display_number: texto(form?.display_number),
        rotulo: texto(form?.rotulo),
        // Vazio vira `null`, e não string vazia: é assim que o cliente volta ao
        // texto padrão da saudação. Aqui apagar é uma ação legítima (o valor
        // está à vista de quem edita), ao contrário das credenciais.
        saudacao: saudacao === '' ? null : saudacao,
        // Pela MESMA regra da saudação: vazio vira `null`, que aqui
        // significa NÃO ENVIAR aquela mensagem. É ação legítima da tela --
        // o valor está à vista de quem edita, ao contrário das credenciais.
        mensagem_pos_departamento: posDepartamento === '' ? null : posDepartamento,
        mensagem_de_encerramento: encerramento === '' ? null : encerramento,
        ativo: Boolean(form?.ativo),
        // SEMPRE presente, e numérico. A API ignora o campo quando ele chega
        // nulo (campo vazio não é instrução), então mandar `''` seria pedir
        // para desligar o agrupamento e não conseguir -- e `Number('')` é 0,
        // que é a instrução OPOSTA. O `|| 0` resolve os dois: desligar é um
        // zero explícito.
        agrupar_por_segundos: Number(form?.agrupar_por_segundos) || 0,
        usa_app_proprio: usaAppProprio,
        // Sempre presente, inclusive vazio: omitir MANTÉM o menu na API, então
        // "tirei todos os departamentos e salvei" não teria efeito nenhum.
        departamentos: Array.isArray(form?.departamentos) ? [...form.departamentos] : [],
    };

    // No cadastro não há o que proteger (não existe valor guardado) e o token é
    // obrigatório, então vai sempre. Na edição, só com o gesto.
    if (!edicao) {
        corpo.token = token;
    } else if (trocarToken && token !== '') {
        corpo.token = token;
    }

    if (usaAppProprio && segredo !== '' && (!edicao || trocarSegredo)) {
        corpo.app_secret = segredo;
    }

    return corpo;
};

/** O status HTTP do erro, nos dois formatos que o `http.js` do projeto entrega. */
export const statusDoErro = (erro) => {
    const bruto = erro?.errorStatus ?? erro?.response?.status;
    const status = Number(bruto);

    return Number.isInteger(status) ? status : null;
};

/** O corpo da resposta de erro, nos dois formatos do `http.js`. */
export const corpoDoErro = (erro) => erro?.responseData ?? erro?.response?.data ?? null;

/**
 * O nome de cada campo em português, para a queixa do servidor poder ser
 * reescrita na língua de quem lê a tela.
 *
 * Bate com o rótulo do campo no formulário de propósito: a mensagem manda o
 * cliente olhar um campo, e ele tem de achar aquele campo com aquele nome.
 */
export const ROTULOS_DOS_CAMPOS = {
    phone_number_id: 'ID do número na Meta (phone_number_id)',
    waba_id: 'ID da conta do WhatsApp Business (waba_id)',
    display_number: 'Número como o cliente o vê',
    rotulo: 'Rótulo',
    saudacao: 'Saudação',
    mensagem_pos_departamento: 'Mensagem após escolher o departamento',
    mensagem_de_encerramento: 'Mensagem de encerramento',
    token: 'Token de acesso',
    app_secret: 'Segredo do app (app_secret)',
    usa_app_proprio: 'App próprio',
    departamentos: 'Menu de departamentos',
    webhook_slug: 'Endereço do webhook',
};

/**
 * Qual REGRA da validação falhou, deduzida do texto que a API mandou.
 *
 * A API roda com `locale => 'en'` e só tem `resources/lang/en`, então o que
 * chega é o texto do Laravel em inglês -- "The phone number id has already been
 * taken." numa tela em português entregue ao cliente final. Reconhecer a regra
 * é o que permite reescrever a frase em português a partir da CHAVE do campo,
 * em vez de repassar o inglês.
 *
 * Os dois idiomas estão nos padrões porque o dia em que a API ganhar
 * `resources/lang/pt_BR` não pode ser o dia em que esta tradução para de
 * funcionar.
 */
const REGRAS = [
    ['unique', /already been taken|j[áa] (est[áa] em uso|foi)/i],
    ['required', /is required|obrigat[óo]ri/i],
    ['max', /(may|must) not be greater than|n[ãa]o pode ser (maior|superior)/i],
    ['boolean', /must be true or false|deve ser (verdadeiro|true)/i],
    // `selected` (ou `selecionado`) é obrigatório no padrão: só `is invalid`
    // casaria também "The :attribute format is invalid." das rules `regex` e
    // `url`, e aí um formato errado seria traduzido como "a opção escolhida não
    // existe nesta empresa" -- mensagem errada, que é pior que mensagem em
    // inglês. Nenhuma das duas rules está neste controller hoje; o padrão
    // apertado é para continuar certo quando estiver.
    ['exists', /the selected .+ is invalid|selecionad[oa].+inv[áa]lid/i],
];

const regraDoTexto = (bruto) => {
    const achada = REGRAS.find(([, padrao]) => padrao.test(bruto));

    return achada ? achada[0] : null;
};

/** A frase em português para cada regra, usando o rótulo do campo. */
const FRASE_DA_REGRA = {
    unique: 'este valor já está cadastrado.',
    required: 'é obrigatório.',
    max: 'o valor é longo demais.',
    boolean: 'só aceita marcado ou desmarcado.',
    exists: 'a opção escolhida não existe nesta empresa.',
};

/**
 * Frases específicas, onde o genérico não bastaria.
 *
 * `phone_number_id` duplicado é a única validação da API que a tela NÃO
 * replica, logo é o 422 mais provável de todos -- e "este valor já está
 * cadastrado" deixaria o cliente procurando o número dele na própria lista, que
 * é do tenant. A colisão pode ser com OUTRA empresa da plataforma (o índice é
 * global, porque é por este id que o webhook da Meta descobre de quem é a
 * mensagem), e nesse caso não há nada que ele possa ver ou consertar sozinho.
 */
const FRASES_ESPECIFICAS = {
    phone_number_id: {
        unique: 'Este ID de número já está cadastrado na plataforma — cada phone_number_id '
            + 'existe uma única vez. Confira o ID no app da Meta; se ele foi cadastrado por '
            + 'outra empresa, fale com o suporte.',
    },
};

/** `departamentos.0` e `departamentos.*` viram `departamentos`. */
const campoBase = (campo) => String(campo).split('.')[0];

const frasePorCampo = (campo, bruto) => {
    const base = campoBase(campo);
    const regra = regraDoTexto(bruto);
    const rotulo = ROTULOS_DOS_CAMPOS[base];

    if (regra && FRASES_ESPECIFICAS[base]?.[regra]) return FRASES_ESPECIFICAS[base][regra];
    if (regra && rotulo) return `${rotulo}: ${FRASE_DA_REGRA[regra]}`;
    // Último recurso: o texto do servidor, cru. É a regra que eu não previ, e
    // perder a informação é pior que mostrá-la na língua errada -- inclusive
    // porque algumas mensagens da API já vêm em português (as que o controller
    // escreve à mão, como a do pedido contraditório).
    if (rotulo) return `${rotulo}: ${bruto}`;
    return bruto;
};

/**
 * As queixas do 422 POR CAMPO, em português: `{ campo: mensagem }`.
 *
 * A tela usa as CHAVES para destacar o campo culpado -- antes ela jogava fora
 * exatamente a parte da resposta que dizia onde estava o problema, e o cliente
 * recebia um texto sem saber em que campo mexer.
 */
export const errosDaApi = (corpo) => {
    const erros = corpo?.errors;

    if (!erros || typeof erros !== 'object') return {};

    const saida = {};

    Object.keys(erros).forEach((campo) => {
        const valor = erros[campo];
        const lista = (Array.isArray(valor) ? valor : [valor])
            .map((item) => texto(item))
            .filter((item) => item !== '');

        if (lista.length === 0) return;

        // `campoBase` é o que AGRUPA: `departamentos.0` e `departamentos.1`
        // falham juntos e viram uma queixa só, no campo `departamentos`, que é o
        // único nome que a tela sabe destacar. Sem o agrupamento seriam duas
        // chaves que não correspondem a campo nenhum do formulário.
        //
        // Qual das sub-queixas sobra é indiferente de propósito: havia aqui um
        // `if (saida[base] === undefined)` para o primeiro ganhar, e a rodada de
        // mutação mostrou que apagá-lo não muda nada observável -- a escolha era
        // arbitrária e o `if` era peso morto.
        saida[campoBase(campo)] = frasePorCampo(campo, lista.join(' '));
    });

    return saida;
};

/**
 * A queixa do 422 como uma frase só, para o alerta do formulário.
 *
 * Em português e apontando os campos, montada a partir de `errosDaApi`.
 */
export const mensagemDeValidacao = (corpo) => {
    const porCampo = errosDaApi(corpo);
    const frases = Object.keys(porCampo).map((campo) => porCampo[campo]);

    if (frases.length > 0) return frases.join(' ');

    return texto(corpo?.message) === '' ? null : texto(corpo.message);
};

/**
 * O 409 do delete, dito de um jeito que resolve.
 *
 * Diz QUANTAS conversas apontam para o número e diz o que fazer no lugar:
 * desativar. Apagar deixaria as salas apontando para um id que não existe, e a
 * resposta do operador simplesmente não sairia -- sem erro na tela dele. Quem
 * descobriria é o cliente do outro lado, esperando.
 *
 * O texto anterior usava como JUSTIFICATIVA da recusa ("as respostas deixariam
 * de sair") exatamente o efeito que atribuía à recomendação ("desative: ele sai
 * do roteamento e do envio") -- se desativar parasse o envio, apagar não teria
 * por que ser pior. A contradição vinha de a frase estar errada: desativar para
 * só a ENTRADA. Conversa já estampada com o número continua sendo respondida
 * por ele, porque a janela de 24h da Meta é do par (número, pessoa). É isso que
 * preserva o histórico de verdade, e é isso que torna apagar inaceitável.
 */
export const mensagemDoConflito = (corpo) => {
    const quantidade = Number(corpo?.atendimentos);
    const temContagem = Number.isFinite(quantidade) && quantidade > 0;
    const contagem = temContagem
        ? `${quantidade} atendimento${quantidade === 1 ? '' : 's'} `
        : 'atendimentos ';

    return `Este número já tem ${contagem}e não pode ser excluído: as conversas ficariam sem `
        + 'origem e as respostas do operador deixariam de sair. Desative o número — ele para de '
        + 'RECEBER mensagem nova e o histórico continua no lugar. As conversas já abertas seguem '
        + 'sendo respondidas por ele; para cortar o envio também, revogue o token na Meta.';
};

/**
 * O erro traduzido para quem está na frente da tela.
 *
 * `fallback` é o texto da AÇÃO ("não foi possível salvar", "não foi possível
 * carregar"): saber o que falhou é metade da informação, e os status sem
 * tratamento próprio não trazem a outra metade.
 */
export const mensagemDoErro = (erro, fallback = 'Não foi possível concluir a operação. Tente novamente.') => {
    const status = statusDoErro(erro);

    if (status === 403) return MENSAGEM_SEM_PERMISSAO;
    if (status === 409) return mensagemDoConflito(corpoDoErro(erro));
    if (status === 422) return mensagemDeValidacao(corpoDoErro(erro)) || fallback;

    return fallback;
};

// ---------------------------------------------------------------------
// Os quatro abaixo viviam no topo de `pages/WhatsAppPage`. Vieram para ca
// quando a edicao do numero ganhou pagina propria: as DUAS telas precisam
// deles, e uma terceira copia de `departamentoAtivo` seria a que divergiria
// no primeiro conserto feito so num lado.
// ---------------------------------------------------------------------

/**
 * As variáveis que os textos da instância aceitam.
 *
 * ## Por que elas aparecem na TELA, e não só na documentação
 *
 * Porque um marcador desconhecido é enviado como está. Quem escrever
 * `{departamanto}` por engano verá a palavra torta chegar no celular do
 * cliente -- e esta lista, ao lado do campo, é o único lugar onde dá para
 * perceber antes.
 *
 * A alternativa (apagar o que não reconhece) seria pior: um texto perderia em
 * silêncio um pedaço que alguém escreveu de propósito.
 *
 * Espelha `AppServicesWhatsAppMensagensDaInstancia::VARIAVEIS`. São duas
 * listas, e isso é uma escolha: o servidor não pode depender da tela para
 * resolver variável, e a tela não pode esperar uma requisição só para saber o
 * que escrever. O teste de contrato prende as duas juntas.
 */
export const VARIAVEIS_DA_MENSAGEM = [
  { nome: 'departamento', descricao: 'Nome do departamento que vai atender' },
  { nome: 'instancia', descricao: 'Rótulo do número (ex.: MAX, SysFar)' },
  { nome: 'cliente', descricao: 'Nome do cliente, quando conhecido' },
  { nome: 'operador', descricao: 'Nome de quem atendeu' },
  { nome: 'protocolo', descricao: 'Identificador da conversa' },
];

/** As variáveis em uma linha, para caber embaixo do campo. */
export const variaveisEmUmaLinha = () =>
  VARIAVEIS_DA_MENSAGEM.map((v) => `{${v.nome}}`).join('  ');

export const formularioVazio = {
  phone_number_id: '',
  waba_id: '',
  display_number: '',
  rotulo: '',
  saudacao: '',
  // As duas mensagens que o cliente recebe depois da saudação. Vazias
  // significam NÃO ENVIAR -- nunca um texto padrão: cada mensagem no
  // WhatsApp é cobrada pela Meta.
  mensagem_pos_departamento: '',
  mensagem_de_encerramento: '',
  // Zero = desligado, e é o padrão: agrupar é uma escolha de atendimento, não
  // um comportamento que todo número novo deva herdar.
  agrupar_por_segundos: 0,
  token: '',
  app_secret: '',
  usa_app_proprio: false,
  ativo: true,
  departamentos: [],
};

/**
 * As opções do agrupamento de mensagens.
 *
 * Fixas, e não um campo numérico livre. O teto do servidor é 300 segundos,
 * mas o que importa aqui é outro limite: passando de uns dois minutos o
 * cliente lê o silêncio como "ninguém me respondeu" e escreve de novo — o que
 * custa uma mensagem de ENTRADA e desfaz a economia.
 */
export const OPCOES_DE_AGRUPAMENTO = [
  { valor: 0, rotulo: 'Desligado — cada mensagem sai na hora' },
  { valor: 10, rotulo: '10 segundos' },
  { valor: 20, rotulo: '20 segundos' },
  { valor: 30, rotulo: '30 segundos' },
  { valor: 45, rotulo: '45 segundos' },
  { valor: 60, rotulo: '1 minuto' },
  { valor: 120, rotulo: '2 minutos' },
];

export const rotuloDoDepartamento = (grupo) =>
  grupo?.description || grupo?.code || `Departamento ${grupo?.id}`;

/**
 * Departamento ATIVO, na mesma leitura que o resto do projeto usa
 * (`components/AdvancedFilterModal`): `status` verdadeiro ou 1.
 *
 * O `GET /groups` devolve todos os `service_groups`, inclusive os desativados.
 * Oferecer um departamento desativado como opção do menu real do cliente final
 * não dá erro em lugar nenhum: a API aceita o vínculo (a regra dela é o
 * `tenant_id`, não o status), o menu vai para o WhatsApp com a opção, e quem
 * escolher cai num departamento que a empresa desligou.
 */
export const departamentoAtivo = (grupo) => grupo?.status === true || grupo?.status === 1;
